import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VioTablesV2Component } from 'vio-tables-v2';
import type { VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const bootstrap: any;

interface Customer {
    _id: string;
    name: string;
    whatsAppNo: string;
}

interface GroupCriteria {
    l1Id: string;
    l1Name: string;
    l2Id: string;
    l2Name: string;
    customers: Customer[];
}

interface RecipientList {
    _id: string;
    listName: string;
    customerCount: number;
    users: Customer[];
    createdAt: string;
}

@Component({
    selector: 'app-recipient-lists',
    standalone: true,
    imports: [CommonModule, FormsModule, VioTablesV2Component],
    templateUrl: './recipient-lists.component.html',
    styleUrl: './recipient-lists.component.scss',
})
export class RecipientListsComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('viewListModal') viewListModalRef?: ElementRef<HTMLElement>;

    // L1 master navigation
    genericMasters: any[] = [];
    isLoadingMasters = false;
    selectedL1: any = null;

    // L2 values for selected L1
    l2Values: any[] = [];
    isLoadingL2 = false;

    // Selected groups (multi-group criteria)
    selectedGroups: GroupCriteria[] = [];
    isLoadingGroup: { [key: string]: boolean } = {};

    // Computed customer pool (union of all groups)
    customerPool: Customer[] = [];
    selectedCustomers: Set<string> = new Set();

    // Save form
    listName = '';
    isSaving = false;
    saveResult = '';
    saveResultSuccess = false;

    // Saved lists
    savedLists: RecipientList[] = [];
    savedListsForTable: any[] = [];
    isLoadingLists = false;

    // View-list modal
    viewListTitle = '';
    viewListCustomers: Customer[] = [];

    savedListsTableConfig: VT2_TABLE_CONFIG = {
        tableTitle: '',
        columnLabels: {
            listName: 'List Name',
            recipients: 'Recipients',
            created: 'Created',
        },
        excludeFields: ['_id', 'users'],
        isSortable: true,
        showFormulaButton: false,
        showSummationButton: false,
        showHeaderGroupButton: false,
        dateFields: ['created'],
    };

    constructor(private apiService: ApiRequestsService) {}

    ngOnInit(): void {
        this.savedListsTableConfig = {
            ...this.savedListsTableConfig,
            actionButtons: [
                {
                    icon: '👥',
                    label: 'View',
                    title: 'View recipients in this list',
                    onClick: (row) => this.showListCustomers(row),
                },
                {
                    icon: '🗑',
                    label: 'Delete',
                    title: 'Delete this list',
                    onClick: (row) => this.deleteList(row),
                },
            ],
        };
        this.loadMasters();
        this.loadSavedLists();
    }

    ngAfterViewInit(): void {
        if (this.viewListModalRef) document.body.appendChild(this.viewListModalRef.nativeElement);
    }

    ngOnDestroy(): void {
        const el = this.viewListModalRef?.nativeElement;
        if (el?.parentNode === document.body) document.body.removeChild(el);
    }

    trackById(index: number, item: any): string { return item._id; }
    trackByIndex(index: number): number { return index; }

    // ── L1 / L2 navigation ──────────────────────────────────────────────────

    loadMasters(): void {
        this.isLoadingMasters = true;
        this.apiService.getApiCall('api/v1/generic-master').subscribe({
            next: (res: any) => {
                this.isLoadingMasters = false;
                this.genericMasters = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
            },
            error: () => { this.isLoadingMasters = false; },
        });
    }

    selectL1(master: any): void {
        if (this.selectedL1?._id === master._id) return;
        this.selectedL1 = master;
        this.l2Values = [];
        this.isLoadingL2 = true;
        this.apiService.getApiCall(`api/v1/generic-master/${master._id}/values`).subscribe({
            next: (res: any) => {
                this.isLoadingL2 = false;
                this.l2Values = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
            },
            error: () => { this.isLoadingL2 = false; },
        });
    }

    // ── Group management ─────────────────────────────────────────────────────

    isGroupAdded(l2: any): boolean {
        return this.selectedGroups.some(g => g.l2Id === l2._id);
    }

    addGroup(l2: any): void {
        if (!this.selectedL1 || this.isGroupAdded(l2)) return;
        const key = l2._id;
        this.isLoadingGroup[key] = true;
        this.apiService.getApiCall(`api/v1/generic-master/${this.selectedL1._id}/values/${l2._id}/customers`).subscribe({
            next: (res: any) => {
                this.isLoadingGroup[key] = false;
                const customers: Customer[] = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
                this.selectedGroups.push({
                    l1Id: this.selectedL1._id,
                    l1Name: this.selectedL1.masterName,
                    l2Id: l2._id,
                    l2Name: l2.value,
                    customers,
                });
                this.computePool();
            },
            error: () => { this.isLoadingGroup[key] = false; },
        });
    }

    removeGroup(g: GroupCriteria): void {
        this.selectedGroups = this.selectedGroups.filter(x => x.l2Id !== g.l2Id);
        this.computePool();
    }

    computePool(): void {
        const seen = new Set<string>();
        const pool: Customer[] = [];
        for (const g of this.selectedGroups) {
            for (const c of g.customers) {
                if (!seen.has(c._id)) {
                    seen.add(c._id);
                    pool.push(c);
                }
            }
        }
        const poolIds = new Set(pool.map(c => c._id));
        this.selectedCustomers.forEach(id => { if (!poolIds.has(id)) this.selectedCustomers.delete(id); });
        pool.forEach(c => { if (!this.selectedCustomers.has(c._id)) this.selectedCustomers.add(c._id); });
        this.customerPool = pool;
    }

    // ── Customer selection ────────────────────────────────────────────────────

    searchQuery = '';

    get filteredPool(): Customer[] {
        const q = this.searchQuery.trim().toLowerCase();
        if (!q) return this.customerPool;
        return this.customerPool.filter(c =>
            c.name.toLowerCase().includes(q) || c.whatsAppNo.includes(q)
        );
    }

    get allFilteredSelected(): boolean {
        const fp = this.filteredPool;
        return fp.length > 0 && fp.every(c => this.selectedCustomers.has(c._id));
    }

    toggleCustomer(c: Customer): void {
        if (this.selectedCustomers.has(c._id)) this.selectedCustomers.delete(c._id);
        else this.selectedCustomers.add(c._id);
    }

    isSelected(c: Customer): boolean { return this.selectedCustomers.has(c._id); }

    selectAll(): void { this.filteredPool.forEach(c => this.selectedCustomers.add(c._id)); }

    deselectAll(): void { this.filteredPool.forEach(c => this.selectedCustomers.delete(c._id)); }

    toggleSelectAll(): void {
        if (this.allFilteredSelected) this.deselectAll();
        else this.selectAll();
    }

    get selectedCount(): number { return this.selectedCustomers.size; }

    get selectedCustomerObjects(): Customer[] {
        return this.customerPool.filter(c => this.selectedCustomers.has(c._id));
    }

    // ── Save ─────────────────────────────────────────────────────────────────

    get canSave(): boolean {
        return !!this.listName.trim() && this.selectedCount > 0 && !this.isSaving;
    }

    saveList(): void {
        if (!this.canSave) return;
        this.isSaving = true;
        this.saveResult = '';
        this.apiService.postApiCall('api/v1/recipient-lists', {
            listName: this.listName.trim(),
            users: this.selectedCustomerObjects.map(c => ({ _id: c._id })),
        }).subscribe({
            next: (res: any) => {
                this.isSaving = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.saveResult = `"${this.listName.trim()}" saved with ${this.selectedCount} recipient(s).`;
                    this.saveResultSuccess = true;
                    this.resetBuilder();
                    this.loadSavedLists();
                } else {
                    this.saveResult = res?.errorDescription || 'Failed to save.';
                    this.saveResultSuccess = false;
                }
            },
            error: () => {
                this.isSaving = false;
                this.saveResult = 'Failed to save. Please check your connection.';
                this.saveResultSuccess = false;
            },
        });
    }

    private resetBuilder(): void {
        this.listName = '';
        this.selectedGroups = [];
        this.customerPool = [];
        this.selectedCustomers = new Set();
        this.searchQuery = '';
        this.selectedL1 = null;
        this.l2Values = [];
    }

    // ── Saved lists ──────────────────────────────────────────────────────────

    loadSavedLists(): void {
        this.isLoadingLists = true;
        this.apiService.getApiCall('api/v1/recipient-lists').subscribe({
            next: (res: any) => {
                this.isLoadingLists = false;
                this.savedLists = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
                this.savedListsForTable = this.savedLists.map(l => ({
                    _id: l._id,
                    listName: l.listName,
                    recipients: l.customerCount,
                    users: l.users,
                    created: l.createdAt,
                }));
            },
            error: () => { this.isLoadingLists = false; },
        });
    }

    showListCustomers(row: any): void {
        this.viewListTitle = row.listName;
        this.viewListCustomers = row.users || [];
        bootstrap.Modal.getOrCreateInstance(this.viewListModalRef!.nativeElement).show();
    }

    deleteList(row: any): void {
        if (!confirm(`Delete "${row.listName}"?`)) return;
        this.apiService.deleteApiCall(`api/v1/recipient-lists/${row._id}`).subscribe({
            next: () => this.loadSavedLists(),
            error: () => {},
        });
    }
}
